/**
 * Eric 2026-06-25 (iter124 — Session A8) — Panneau Q&R (Questions/Réponses).
 *
 * Extrait de VideoDetailPage.js (refacto P0). Affiche les Q&R extraites par
 * le pipeline IA + le mode édition inline + l'import .docx + le rapport.
 * Gating canEdit/isAdmin (Vincent #336 SPEC STRICK).
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../../lib/uiDialogs";
import { CheckCircle, FloppyDisk, PencilSimple, Trash, X } from "@phosphor-icons/react";
import { formatTitleHistoryEntry } from "../../lib/knowledgeFormat";
import KUFavoriteHeart from "./KUFavoriteHeart";
import KnowledgeCleanupModal from "./KnowledgeCleanupModal";  // Tâche 1284
import { useFavoriteKuIds } from "../../hooks/useFavoriteKuIds";

export const QAPanel = ({
  show, canEdit, isAdmin, lang, api, videoId,
  video, knowledgeUnits, allTranscripts, pipelineHasEverRun, isTextDoc,
  qaSubtab, setQaSubtab,
  qaKeepPrevious, setQaKeepPrevious,
  qaTitleAi,
  setQaClearHistoryConfirm,
  setQaHistoryOpenForKuId, qaHistoryOpenForKuId,
  editingInlineKuId, setEditingInlineKuId,
  inlineKuDraft, setInlineKuDraft,
  handleQaGenerateTitle,
  setImportOpen,
  setDetailModalKu,
  aiJobProgress, setAiJobProgress,
  loadVideoData, openReportModal, seekTo,
  pollAgentJob,
}) => {
  // Vincent B614 (Eric 2026-07-29 SPEC STRICK) — Set des KUs favorites de
  // l'utilisateur pour afficher le ♥ à gauche de chaque Q&R.
  const favKuIds = useFavoriteKuIds();
  // Tâche 1284 — Nettoyage des Q&R orphelines : la modale propose, l'humain
  // accepte ou refuse ligne par ligne.
  const [cleanupOpen, setCleanupOpen] = useState(false);
  if (!show) return null;
  return (
              <div className="flex flex-col" data-testid="qa-panel">
                {/* Vincent bug #84 (2026-05-08) — Bandeau de statut style "Locuteurs" :
                    compteurs et statut du pipeline en haut, avant les sous-onglets. */}
                {(() => {
                  const qaTotal = (knowledgeUnits || []).filter(k => k.type === "qa").length;
                  const qaApproved = (knowledgeUnits || []).filter(k => k.type === "qa" && k.status === "approved").length;
                  const qaPending = qaTotal - qaApproved;
                  // Enhancement B614.d (Eric 2026-07-30) — Compteur global
                  // « ❤️ N favoris » en tête de chaque onglet Q&R/Scripts/Procédures/
                  // Objections/Concepts. Permet à l'apprenant de repérer d'un coup
                  // d'œil combien de ressources il a marquées dans cette catégorie
                  // pour cette vidéo — utile en session de révision.
                  const qaFavorites = (knowledgeUnits || []).filter(k => k.type === "qa" && favKuIds.has(k.id)).length;
                  return (
                    <div className="px-4 py-2.5 bg-teal-50/50 border-b border-teal-200 flex items-center justify-between gap-3 text-[11px]" data-testid="qa-status-banner">
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-teal-800">{qaTotal} Q&R</span>
                        <span className="text-teal-700">✓ {qaApproved} {lang === "fr" ? "révisée(s)" : "reviewed"}</span>
                        {qaPending > 0 && <span className="text-amber-700">⏳ {qaPending} {lang === "fr" ? "à réviser" : "pending"}</span>}
                        {qaFavorites > 0 && (
                          <span className="text-rose-700" data-testid="qa-favorites-count">♥ {qaFavorites} {lang === "fr" ? (qaFavorites > 1 ? "favoris" : "favori") : (qaFavorites > 1 ? "favorites" : "favorite")}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {qaTotal === 0 && !pipelineHasEverRun && (
                          <span className="text-teal-700/80 italic">{lang === "fr" ? "Pipeline non lancé" : "Pipeline not run"}</span>
                        )}
                        {/* Vincent XLSX 2026-07-15 Row 486 (Eric 2026-07-16 SPEC STRICK) —
                            Bouton « Lancer / Relancer » AFFICHÉ SEULEMENT SI le pipeline n'a
                            JAMAIS été lancé. Une fois pipeline a été exécuté au moins une
                            fois, on ne montre plus ce shortcut : l'utilisateur utilise le
                            gros bouton « Générer à nouveau… » dans la pipeline-box de
                            l'onglet Pipeline. Évite la confusion « pourquoi 2 boutons ? ». */}
                        {canEdit && !pipelineHasEverRun && (
                          <button
                            onClick={() => setQaSubtab("pipeline")}
                            className="px-2 py-1 rounded text-[10px] font-bold bg-teal-600 hover:bg-teal-700 text-white transition-colors"
                            data-testid="qa-banner-pipeline-shortcut"
                          >
                            ⚗️ {qaTotal === 0 ? (lang === "fr" ? "Lancer" : "Run") : (lang === "fr" ? "Relancer" : "Re-run")}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}
                {/* Vincent B839 (Eric 2026-02-14 SPEC STRICK PROD) —
                    Exactement 2 sous-onglets pour TOUS les utilisateurs :
                      - Pipeline (visible pour tous ; les boutons à
                        l'intérieur sont eux gated isAdmin)
                      - Modifier / Visualiser (label dépend de canEdit,
                        valeur interne "edit" ou "view")
                    Le sous-onglet "Visualiser" séparé pour non-éditeurs
                    est retiré (il faisait 1 seul onglet au total, ce
                    qui contredisait la parité voulue avec Procédures).
                    Vincent B848 (Eric 2026-02-14 SPEC STRICK PROD) —
                    Le bouton Pipeline n'est plus visible pour les
                    non-éditeurs (parité stricte 5 panneaux). */}
                <div className="flex border-b border-zinc-200 bg-zinc-50">
                  {canEdit && (
                    <button
                      onClick={() => setQaSubtab("pipeline")}
                      className={`flex-1 py-2 text-xs font-medium transition-colors relative ${
                        qaSubtab === "pipeline" ? "text-teal-700 bg-white" : "text-zinc-500 hover:text-zinc-700"
                      }`}
                      data-testid="qa-subtab-pipeline"
                    >
                      ⚗️ {lang === "fr" ? "Pipeline" : "Pipeline"}
                      {qaSubtab === "pipeline" && <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-teal-500 rounded-full" />}
                    </button>
                  )}
                  <button
                    onClick={() => setQaSubtab(canEdit ? "edit" : "view")}
                    className={`flex-1 py-2 text-xs font-medium transition-colors relative ${
                      (qaSubtab === "edit" || qaSubtab === "view") ? "text-teal-700 bg-white" : "text-zinc-500 hover:text-zinc-700"
                    }`}
                    data-testid={canEdit ? "qa-subtab-edit" : "qa-subtab-view"}
                  >
                    {canEdit
                      ? <>📋 {lang === "fr" ? "Liste des Q&R" : "Q&A list"}</>
                      : <>👁 {lang === "fr" ? "Visualiser" : "View"}</>}
                    {(qaSubtab === "edit" || qaSubtab === "view") && <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-teal-500 rounded-full" />}
                  </button>
                </div>
                <div className="p-4 space-y-4">
                  {(qaSubtab === "view" || qaSubtab === "edit" || qaSubtab === "pipeline" || qaSubtab === "generate") && (() => {
                    const qaCount = (knowledgeUnits || []).filter(k => k.type === "qa").length;
                    const hasRun = qaCount > 0;
                    // Vincent #107 (2026-05-08) — Détection "transcription modifiée après dernière exécution du pipeline"
                    const lastQaAt = Math.max(0, ...(knowledgeUnits || []).filter(k => k.type === "qa").map(k => new Date(k.created_at || 0).getTime()));
                    const mergedUpdatedAt = new Date(allTranscripts?.merged?.updated_at || allTranscripts?.merged?.created_at || 0).getTime();
                    const transcriptModifiedAfterRun = hasRun && mergedUpdatedAt > 0 && mergedUpdatedAt > lastQaAt;
                    return (
                    <div className="space-y-3" data-testid="qa-generate-view">
                      {/* Vincent #107 (2026-05-08) — Section Pipeline : description + boîte pâle (Générer | Rapport). */}
                      {(qaSubtab === "pipeline" || qaSubtab === "generate") && (
                        <>
                      <p className="text-xs text-zinc-600">{lang === "fr"
                        ? `Pipeline IA dédié qui extrait les paires Question/Réponse de ${isTextDoc ? "ce texte" : "cette vidéo"} (analyse → filtrage anodin/qualité → déduplication). Durée : 1 à 3 min.`
                        : `Dedicated AI pipeline extracting Q&A pairs from this ${isTextDoc ? "text" : "video"}. Duration: 1 to 3 min.`}
                      </p>
                      {transcriptModifiedAfterRun && (
                        <div className="px-3 py-2 rounded border border-amber-300 bg-amber-50 text-[11px] text-amber-900 flex items-start gap-2" data-testid="qa-transcript-modified-warning">
                          <span>⚠️</span>
                          <span>{lang === "fr"
                            ? "La transcription fusionnée a été modifiée depuis la dernière exécution du pipeline. Cliquez « Générer à nouveau » pour mettre à jour les Q&R."
                            : "The merged transcript has been modified since the last pipeline run. Click 'Generate again' to refresh Q&R."}
                          </span>
                        </div>
                      )}
                      {isAdmin && (
                        <div className="rounded-lg border border-teal-200 bg-teal-50/60 p-3 space-y-2" data-testid="qa-pipeline-box">
                          {/* Vincent 2026-05-06 (bug #58) — option conserver/supprimer anciennes Q&R */}
                          {hasRun && (
                            <label className="flex items-center gap-2 text-xs text-zinc-700">
                              <input
                                type="checkbox"
                                checked={qaKeepPrevious}
                                onChange={(e) => setQaKeepPrevious(e.target.checked)}
                                data-testid="qa-keep-previous-checkbox"
                                className="rounded"
                              />
                              {lang === "fr"
                                ? `Conserver les ${qaCount} Q&R déjà générées (sinon elles seront supprimées avant relance)`
                                : `Keep existing ${qaCount} Q&R (otherwise delete before relaunch)`}
                            </label>
                          )}
                          {/* Vincent B839 (Eric 2026-02-14 SPEC STRICK PROD)
                              — Grille 2×2 EXACTE :
                                 [ Générer/Régénérer | Effacer ]
                                 [ Rapport           | Importer ] */}
                          <div className="grid grid-cols-2 gap-2">
                          <button
                            onClick={async () => {
                              const ok = await confirmDialog(lang === "fr"
                                ? (hasRun
                                    ? `Relancer le pipeline Q&R ? (1-3 min)\n\n${qaKeepPrevious ? "Les anciennes Q&R seront conservées." : "Les " + qaCount + " Q&R actuelles seront SUPPRIMÉES avant relance."}`
                                    : "Générer les Q&R pour cette vidéo via le pipeline IA (1-3 minutes) ?\n\nLes Q&R seront créées en statut « en révision ».")
                                : "Run/Rerun Q&R pipeline?", { tone: "warning" });
                              if (!ok) return;
                              try {
                                // Vincent 2026-05-06 (bug #58): option delete previous
                                if (hasRun && !qaKeepPrevious) {
                                  await axios.delete(`${api}/api/agent/knowledge-units/by-video/${videoId}?type=qa`, { withCredentials: true });
                                }
                                toast.info(lang === "fr" ? "Pipeline Q&R lancé en arrière-plan (1-3 min)..." : "Q&R pipeline running in background...");
                                // Eric (2026-06-12) — mode background + polling :
                                // évite la coupure Cloudflare (~100 s) en production.
                                // Eric (2026-06-14) — Barre de progression visuelle :
                                // on alimente `aiJobProgress` à chaque tick (chunks_processed/total_chunks).
                                setAiJobProgress({ kind: "qa", chunks_processed: 0, total_chunks: 0, step: "starting" });
                                const start = await axios.post(`${api}/api/agent/generate-qa-batch/${videoId}?background=1`, {}, { withCredentials: true, timeout: 30000 });
                                const rdata = await pollAgentJob(start.data.job_id, {
                                  onProgress: (p) => setAiJobProgress({ kind: "qa", ...p }),
                                });
                                setAiJobProgress(null);
                                const detail = `${rdata.skipped_anodin || 0} anodins, ${rdata.skipped_low_quality || 0} non substantiels, ${rdata.skipped_duplicate || 0} doublons écartés`;
                                const createdMsg = rdata.drafts_created || 0;
                                if (createdMsg === 0) {
                                  toast.warning(`⚠️ Aucune Q&R créée. ${detail}.`, { duration: 10000 });
                                } else {
                                  toast.success(`${createdMsg} Q&R créées — ${detail}.`, { duration: 9000 });
                                }
                                // Reload knowledgeUnits
                                loadVideoData();
                              } catch (e) {
                                setAiJobProgress(null);
                                toast.error(`Erreur : ${e.response?.data?.detail || e.message}`);
                              }
                            }}
                            className="px-3 py-2.5 rounded-lg border-2 border-teal-300 bg-white hover:bg-teal-100 text-teal-800 transition-all text-xs font-medium leading-tight"
                            data-testid="generate-qa-batch-btn"
                          >
                            ⚗️ {lang === "fr"
                              ? (hasRun ? "Générer à nouveau les Q/R" : "Générer les Q/R")
                              : (hasRun ? "Generate Q&R again" : "Generate Q&R")}
                          </button>
                          <button
                            onClick={async () => {
                              // Vincent B648 v2 (Eric 2026-08-01 SPEC STRICK PROD)
                              // — Bouton dédié pour effacer les Q&R sans être
                              // obligé de passer par « Générer sans conserver ».
                              // Idem que celui déjà en place pour Concepts et
                              // Objections.
                              const count = (knowledgeUnits || []).filter((k) => k.type === "qa").length;
                              if (!count) {
                                toast.info(lang === "fr" ? "Aucune Q&R à effacer" : "No Q&R to clear");
                                return;
                              }
                              const ok = await confirmDialog(lang === "fr"
                                ? `Effacer définitivement les ${count} Q&R existante(s) ? Cette action est irréversible.`
                                : `Permanently delete the ${count} existing Q&R(s)? This cannot be undone.`);
                              if (!ok) return;
                              try {
                                const r = await axios.delete(`${api}/api/agent/knowledge-units/by-video/${videoId}?type=qa`, { withCredentials: true });
                                toast.success(lang === "fr"
                                  ? `${r?.data?.count || 0} Q&R effacée(s)`
                                  : `${r?.data?.count || 0} Q&R cleared`);
                                loadVideoData();
                              } catch (e) {
                                toast.error(e.response?.data?.detail || "Erreur suppression Q&R");
                              }
                            }}
                            className="px-3 py-2.5 rounded-lg border-2 border-red-300 bg-white hover:bg-red-100 text-red-800 transition-all text-xs font-medium leading-tight"
                            data-testid="clear-qa-btn"
                            title={lang === "fr" ? "Effacer toutes les Q&R de cette vidéo" : "Clear all Q&R for this video"}
                          >
                            🗑 {lang === "fr" ? "Effacer les Q&R" : "Clear Q&R"}
                          </button>
                          <button
                            onClick={() => openReportModal('qa')}
                            disabled={!hasRun}
                            className="px-3 py-2.5 rounded-lg border-2 border-blue-300 bg-white hover:bg-blue-100 text-blue-800 transition-all text-xs font-medium leading-tight disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="qa-report-btn"
                            title={hasRun ? (lang === "fr" ? "Voir le rapport de génération en markdown" : "View generation report") : (lang === "fr" ? "Aucun pipeline exécuté" : "No pipeline run yet")}
                          >
                            📄 {lang === "fr" ? "Rapport" : "Report"}
                          </button>
                          <button
                            onClick={() => setImportOpen(true)}
                            className="px-3 py-2.5 rounded-lg border-2 border-[#002FA7] bg-white hover:bg-blue-50 text-[#002FA7] transition-all text-xs font-medium leading-tight"
                            data-testid="qa-import-faq-btn"
                            title={lang === "fr"
                              ? "Importer une FAQ depuis un fichier (CSV/XLSX exporté de Google Sheet, JSON, .docx, .md)"
                              : "Import a FAQ from a file (CSV/XLSX from Google Sheets, JSON, .docx, .md)"}
                          >
                            📥 {lang === "fr" ? "Importer Q&R (fichier Word .docx)" : "Import Q&R (Word .docx file)"}
                          </button>
                          {/* Tâche 1234 (Eric 2026-06-26) — Le rapport Word
                              existait côté serveur mais aucun bouton n'y menait :
                              personne ne pouvait l'atteindre. Le « Rapport »
                              ci-dessus ouvre le markdown de génération ; celui-ci
                              télécharge les Q&R en .docx, prêt à relire ou à
                              transmettre. */}
                          <button
                            onClick={() => {
                              const url = `${api}/api/videos/${videoId}/qa-report.docx?status_filter=all`;
                              window.open(url, "_blank", "noopener");
                            }}
                            disabled={(knowledgeUnits || []).filter((k) => k.type === "qa").length === 0}
                            className="px-3 py-2.5 rounded-lg border-2 border-emerald-300 bg-white hover:bg-emerald-50 text-emerald-800 transition-all text-xs font-medium leading-tight disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="qa-report-docx-btn"
                            title={lang === "fr"
                              ? "Télécharger toutes les Q&R de cette vidéo dans un document Word"
                              : "Download every Q&A of this video as a Word document"}
                          >
                            📝 {lang === "fr" ? "Télécharger en Word" : "Download as Word"}
                          </button>
                          {/* Tâche 1284 (Eric 2026-06-30) — « Nettoyer » :
                              repère les Q&R orphelines (sans réponse, réponse
                              vide, doublon) et les soumet à la revue. Rien
                              n'est effacé sans décision humaine. */}
                          <button
                            onClick={() => setCleanupOpen(true)}
                            disabled={(knowledgeUnits || []).filter((k) => k.type === "qa").length === 0}
                            className="px-3 py-2.5 rounded-lg border-2 border-amber-300 bg-white hover:bg-amber-50 text-amber-800 transition-all text-xs font-medium leading-tight disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="qa-cleanup-btn"
                            title={lang === "fr"
                              ? "Repérer les Q&R orphelines ou inexploitables et choisir celles à supprimer"
                              : "Find orphan or unusable Q&A and choose which ones to delete"}
                          >
                            🧹 {lang === "fr" ? "Nettoyer les Q&R" : "Clean Q&A"}
                          </button>
                          </div>
                          {/* Vincent #19 batch FLHQ 2026-06-10 (Eric 2026-02-06)
                              — Bouton "Régénérer titres" RETIRÉ : Vincent ne veut pas
                              tous les regénérer en bloc (un par un suffit via l'éditeur
                              individuel de chaque Q&R). B839 : Importer intégré à la
                              grille 2×2 ci-dessus (auparavant sur une ligne séparée). */}
                          {/* Vincent #3 (Eric 2026-02-06) — Bouton bulk "Tous quiz"
                              SUPPRIMÉ. Remplacé par une case à cocher "tous quiz"
                              PAR QUESTION dans le modal d'édition (KnowledgeDetailModal). */}
                        </div>
                      )}
                      </>
                      )}

                      {/* Vincent 2026-05-06 (bug #51) — Vincent #81 (2026-05-08) :
                          afficher la liste en mode VIEW (lecture seule, clic = modal) ou
                          EDIT (boutons inline ✎/✓/✗). */}
                      {/* Vincent B848 (Eric 2026-02-14 SPEC STRICK PROD) — Message
                          vide dans le sous-onglet Visualiser pour les non-éditeurs
                          (qui n'ont plus accès au sous-onglet Pipeline). */}
                      {!hasRun && !canEdit && (qaSubtab === "view" || qaSubtab === "edit") && (
                        <div className="text-center py-12 text-sm text-zinc-500 px-4" data-testid="qa-empty-view">
                          {lang === "fr"
                            ? "Aucune Q&R identifiée dans cette vidéo pour le moment."
                            : "No Q&R identified in this video yet."}
                        </div>
                      )}
                      {hasRun && (qaSubtab === "view" || qaSubtab === "edit" || qaSubtab === "generate") && (
                        <div className="border-t border-border pt-3 mt-3 space-y-2" data-testid="qa-inline-list">
                          <div className="text-xs font-medium text-zinc-700">
                            {lang === "fr" ? `${qaCount} Q&R générées pour cette vidéo :` : `${qaCount} Q&R generated for this video:`}
                          </div>
                          {/* Vincent XLSX 2026-07-07 22h Row #10 (Eric 2026-07-08
                              SPEC STRICK) — La hauteur DOIT s'adapter à la
                              fenêtre du navigateur (comme l'onglet Concepts,
                              KnowledgeTab.js `max-h-[calc(100vh-280px)]`).
                              L'ancienne valeur `max-h-80` (320 px) était fixe
                              et forçait un scroll interne minuscule sur les
                              grands écrans. */}
                          <div className="max-h-[calc(100vh-320px)] min-h-[300px] overflow-y-auto space-y-1.5" data-testid="qa-inline-scrollable">
                            {knowledgeUnits.filter(k => k.type === "qa").map((ku) => {
                              const isPending = ku.status === "pending_review";
                              const isEditing = editingInlineKuId === ku.id;
                              return (
                              <div
                                key={ku.id}
                                onClick={(e) => {
                                  // Vincent B699 audit XLS 2026-08-10 (Eric
                                  // 2026-02-10) — Uniformisation avec
                                  // Base de connaissances + ProceduresPanel :
                                  // un clic sur la carte Q&R ouvre le
                                  // KnowledgeDetailModal partagé (avec
                                  // Précédent/Suivant). Le seek au
                                  // timestamp reste dispo via le badge
                                  // timestamp dédié + le bouton play.
                                  // Ancien code : seekTo direct → pas
                                  // de nav modal.
                                  if (isEditing) return;
                                  if (e.target.closest('button, input, textarea, a, [data-no-seek]')) return;
                                  if (typeof setDetailModalKu === "function") {
                                    setDetailModalKu(ku);
                                  }
                                }}
                                className={`flex items-start gap-2 p-2 rounded border border-border hover:bg-secondary/30 transition-colors ${isEditing ? "" : "cursor-pointer hover:border-teal-300"}`}
                                data-testid={`inline-qa-${ku.id}`}
                                data-focus-target={ku.id}
                              >
                                {/* Vincent B614 (Eric 2026-07-29 SPEC STRICK) —
                                    ♥ favori à gauche de chaque Q&R (mode
                                    « Modifier »). Le clic sur le cœur ne
                                    déclenche pas de seekTo (stopPropagation). */}
                                <KUFavoriteHeart
                                  unitId={ku.id}
                                  initialIsFavorite={favKuIds.has(ku.id)}
                                  testIdPrefix="fav-qa-btn"
                                  className="mt-0.5"
                                />
                                <div className="flex-1 min-w-0">
                                  {isEditing ? (
                                    <div className="space-y-1.5">
                                      <div className="flex items-center gap-2">
                                        <input
                                          value={inlineKuDraft.title}
                                          onChange={(e) => setInlineKuDraft(d => ({ ...d, title: e.target.value }))}
                                          className="flex-1 text-xs px-2 py-1 border border-zinc-300 rounded"
                                          placeholder={lang === "fr" ? "Titre" : "Title"}
                                          data-testid={`inline-edit-title-${ku.id}`}
                                        />
                                        <button
                                          type="button"
                                          onClick={(e) => { e.stopPropagation(); handleQaGenerateTitle(ku); }}
                                          disabled={qaTitleAi.generating && qaTitleAi.kuId === ku.id}
                                          className="text-[11px] px-2 py-1 bg-violet-600 text-white rounded hover:bg-violet-700 disabled:opacity-60 whitespace-nowrap"
                                          data-testid={`inline-qa-gen-title-${ku.id}`}
                                          title={lang === "fr" ? "Générer un titre IA" : "Generate AI title"}
                                        >
                                          {qaTitleAi.generating && qaTitleAi.kuId === ku.id ? "⏳ Génération…" : "✨ Générer titre auto"}
                                        </button>
                                        {/* Vincent #110 (Eric 2026-05-15) — Bouton Historique TOUJOURS visible
                                            en mode édition (même quand history.length === 0) pour signaler la
                                            fonctionnalité à l'utilisateur. Le compteur indique combien d'anciens
                                            titres sont rappelables. Cliquer ouvre le dropdown ci-dessous. */}
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setQaHistoryOpenForKuId(prev => prev === ku.id ? null : ku.id);
                                          }}
                                          disabled={qaTitleAi.kuId !== ku.id || qaTitleAi.history.length === 0}
                                          className={`text-[11px] px-2 py-1 rounded whitespace-nowrap border ${qaTitleAi.kuId === ku.id && qaTitleAi.history.length > 0 ? 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100' : 'bg-zinc-50 border-zinc-200 text-zinc-400 cursor-not-allowed'}`}
                                          data-testid={`inline-qa-history-toggle-${ku.id}`}
                                          title={qaTitleAi.kuId === ku.id && qaTitleAi.history.length > 0
                                            ? (lang === "fr" ? `Voir l'historique (${qaTitleAi.history.length} ancien(s))` : `Show history (${qaTitleAi.history.length})`)
                                            : (lang === "fr" ? "Aucun ancien titre à afficher" : "No previous titles")}
                                        >
                                          🕐 {qaTitleAi.kuId === ku.id ? qaTitleAi.history.length : 0}
                                        </button>
                                      </div>

                                      {/* Vincent #139 — Propositions IA cliquables */}
                                      {qaTitleAi.kuId === ku.id && qaTitleAi.proposals.length > 0 && (
                                        <div className="space-y-1 pl-1" data-testid={`inline-qa-proposals-${ku.id}`}>
                                          <p className="text-[10px] uppercase tracking-wide text-zinc-500 font-semibold">{lang === "fr" ? "Propositions IA — cliquez pour utiliser" : "AI proposals — click to use"}</p>
                                          {qaTitleAi.proposals.map((p, pi) => (
                                            <button
                                              key={pi}
                                              type="button"
                                              onClick={(e) => { e.stopPropagation(); setInlineKuDraft(d => ({ ...d, title: p })); }}
                                              className="w-full text-left px-2 py-1 text-[11px] rounded border border-violet-200 bg-violet-50 hover:bg-violet-100 transition-colors"
                                              data-testid={`inline-qa-proposal-${ku.id}-${pi}`}
                                            >
                                              <span className="text-violet-600 mr-1">→</span>{p}
                                            </button>
                                          ))}
                                        </div>
                                      )}

                                      {/* Vincent #139 — Historique cliquable + bouton Tout effacer */}
                                      {/* Vincent #110 (Eric 2026-05-15) — Dropdown historique titres,
                                          visible uniquement quand explicitement togglé via le bouton 🕐. */}
                                      {qaTitleAi.kuId === ku.id && qaTitleAi.history.length > 0 && qaHistoryOpenForKuId === ku.id && (
                                        <div className="space-y-1 pl-1" data-testid={`inline-qa-history-${ku.id}`}>
                                          <div className="flex items-center justify-between">
                                            <p className="text-[10px] uppercase tracking-wide text-zinc-500 font-semibold">
                                              {lang === "fr" ? `Historique (${qaTitleAi.history.length})` : `History (${qaTitleAi.history.length})`}
                                            </p>
                                            <button
                                              type="button"
                                              onClick={(e) => { e.stopPropagation(); setQaClearHistoryConfirm(ku); }}
                                              className="text-[10px] text-rose-600 hover:underline"
                                              data-testid={`inline-qa-history-clear-${ku.id}`}
                                            >
                                              🗑 {lang === "fr" ? "Tout effacer" : "Clear all"}
                                            </button>
                                          </div>
                                          {qaTitleAi.history.slice().reverse().map((h, hi) => (
                                            <button
                                              key={hi}
                                              type="button"
                                              onClick={(e) => { e.stopPropagation(); setInlineKuDraft(d => ({ ...d, title: h.title })); toast.info(lang === "fr" ? "Titre restauré — sauvegardez pour confirmer" : "Title restored — save to confirm"); }}
                                              className="w-full text-left px-2 py-1 text-[11px] rounded border border-amber-200 bg-amber-50 hover:bg-amber-100 transition-colors"
                                              data-testid={`inline-qa-history-${ku.id}-${hi}`}
                                              title={lang === "fr" ? "Cliquez pour revenir à ce titre" : "Click to revert to this title"}
                                            >
                                              <span className="text-amber-600 mr-1">⟲</span>
                                              {/* Vincent 20/05/2026 — Format "AAAA-MM-JJ HH:MM : titre" */}
                                              {h.replaced_at && (
                                                <span className="text-zinc-500 font-mono text-[10px] mr-1">
                                                  {formatTitleHistoryEntry(h.replaced_at)} :
                                                </span>
                                              )}
                                              <span>{h.title}</span>
                                            </button>
                                          ))}
                                        </div>
                                      )}

                                      <textarea
                                        value={inlineKuDraft.question}
                                        onChange={(e) => setInlineKuDraft(d => ({ ...d, question: e.target.value }))}
                                        rows={2}
                                        className="w-full text-xs px-2 py-1 border border-zinc-300 rounded resize-y"
                                        placeholder="Question"
                                        data-testid={`inline-edit-question-${ku.id}`}
                                      />
                                      <textarea
                                        value={inlineKuDraft.answer}
                                        onChange={(e) => setInlineKuDraft(d => ({ ...d, answer: e.target.value }))}
                                        rows={3}
                                        className="w-full text-xs px-2 py-1 border border-zinc-300 rounded resize-y"
                                        placeholder={lang === "fr" ? "Réponse" : "Answer"}
                                        data-testid={`inline-edit-answer-${ku.id}`}
                                      />
                                    </div>
                                  ) : (
                                    <>
                                      <div className="text-xs font-medium truncate">{ku.title || "Untitled"}</div>
                                      <div className="text-xs text-zinc-500 truncate">
                                        Q : {ku.content?.question?.slice(0, 80) || "?"}
                                      </div>
                                    </>
                                  )}
                                </div>
                                {/* Vincent 2026-06-01 — Badge statut GAUCHE retiré
                                    (doublon avec le badge à droite après les boutons).
                                    Seul le badge de droite (ligne ~3667) est conservé. */}
                                {/* Vincent bug #75 (2026-05-07) — 3 boutons dans l'ordre :
                                    éditer (pen) → accepter (check) → refuser (croix).
                                    Les boutons accepter/refuser n'apparaissent que pour les drafts
                                    pending_review ; sinon seul le bouton éditer + supprimer reste. */}
                                {isEditing ? (
                                  <>
                                    <button
                                      type="button"
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                          const payload = {};
                                          if (inlineKuDraft.title !== (ku.title || "")) payload.title = inlineKuDraft.title;
                                          if (inlineKuDraft.question !== (ku.content?.question || "")) payload.question = inlineKuDraft.question;
                                          if (inlineKuDraft.answer !== (ku.content?.answer || "")) payload.answer = inlineKuDraft.answer;
                                          if (Object.keys(payload).length === 0) {
                                            setEditingInlineKuId(null);
                                            return;
                                          }
                                          await axios.patch(`${api}/api/agent/knowledge-units/${ku.id}`, payload, { withCredentials: true });
                                          toast.success(lang === "fr" ? "Q&R mise à jour" : "Q&R updated");
                                          setEditingInlineKuId(null);
                                          loadVideoData();
                                        } catch (err) {
                                          toast.error(`Erreur : ${err.response?.data?.detail || err.message}`);
                                        }
                                      }}
                                      className="text-xs text-teal-600 hover:bg-teal-50 px-1.5 py-0.5 rounded inline-flex items-center"
                                      title={lang === "fr" ? "Sauvegarder" : "Save"}
                                      data-testid={`inline-save-${ku.id}`}
                                    ><FloppyDisk size={14} weight="bold" /></button>
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); setEditingInlineKuId(null); }}
                                      className="text-xs text-zinc-500 hover:bg-zinc-100 px-1.5 py-0.5 rounded inline-flex items-center"
                                      title={lang === "fr" ? "Annuler" : "Cancel"}
                                      data-testid={`inline-cancel-${ku.id}`}
                                    ><X size={14} weight="bold" /></button>
                                  </>
                                ) : (
                                  <>
                                    {/* Vincent bug #75 (2026-05-08) — Ouvre le modal "comme base de connaissance" */}
                                    {/* Vincent #200/#201/#202 (Eric 2026-05-28) — Icônes Q&R unifiées :
                                        - Loupe (🔍) RETIRÉE → doublon avec le crayon (ouvrent le même modal)
                                        - Croix XCircle RETIRÉE → unifiée avec corbeille (Trash gère pending+validé)
                                        - Ordre imposé Vincent : crayon · ✓ (si pending) · corbeille · badge statut
                                        - Crayon toujours visible (même hors mode édition) car il REMPLACE la loupe
                                    */}
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setDetailModalKu(ku);
                                      }}
                                      className="text-xs text-blue-600 hover:bg-blue-50 px-1.5 py-0.5 rounded inline-flex items-center"
                                      title={lang === "fr" ? "Éditer / Voir détail" : "Edit / View details"}
                                      data-testid={`edit-qa-${ku.id}`}
                                    ><PencilSimple size={14} weight="bold" /></button>
                                    {isPending && (qaSubtab === "edit" || qaSubtab === "generate") && (
                                      <button
                                        type="button"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            await axios.post(`${api}/api/agent/draft-queue/${ku.id}/decide`, { decision: "approve" }, { withCredentials: true });
                                            toast.success(lang === "fr" ? "Q&R validée" : "Q&R validated");
                                            loadVideoData();
                                          } catch (err) { toast.error(`Erreur : ${err.response?.data?.detail || err.message}`); }
                                        }}
                                        className="text-xs text-teal-600 hover:bg-teal-50 px-1.5 py-0.5 rounded inline-flex items-center"
                                        title={lang === "fr" ? "Valider" : "Validate"}
                                        data-testid={`approve-qa-${ku.id}`}
                                      ><CheckCircle size={14} weight="bold" /></button>
                                    )}
                                    {(qaSubtab === "edit" || qaSubtab === "generate") && (
                                      <button
                                        type="button"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          const confirmMsg = isPending
                                            ? (lang === "fr" ? "Refuser cette Q&R ?" : "Reject this Q&R?")
                                            : (lang === "fr" ? "Supprimer cette Q&R ?" : "Delete this Q&R?");
                                          const ok = await confirmDialog(confirmMsg);
                                          if (!ok) return;
                                          try {
                                            if (isPending) {
                                              await axios.post(`${api}/api/agent/draft-queue/${ku.id}/decide`, { decision: "reject" }, { withCredentials: true });
                                              toast.success(lang === "fr" ? "Q&R refusée" : "Q&R rejected");
                                            } else {
                                              await axios.delete(`${api}/api/agent/knowledge-units/${ku.id}`, { withCredentials: true });
                                              toast.success(lang === "fr" ? "Q&R supprimée" : "Q&R deleted");
                                            }
                                            loadVideoData();
                                          } catch (err) { toast.error(`Erreur : ${err.response?.data?.detail || err.message}`); }
                                        }}
                                        className="text-xs text-red-500 hover:bg-red-50 px-1.5 py-0.5 rounded inline-flex items-center"
                                        title={isPending ? (lang === "fr" ? "Refuser" : "Reject") : (lang === "fr" ? "Supprimer" : "Delete")}
                                        data-testid={isPending ? `reject-qa-${ku.id}` : `delete-qa-${ku.id}`}
                                      ><Trash size={14} weight="bold" /></button>
                                    )}
                                    {/* Vincent #202 — Badge statut : "..." jaune si pending, ✓ vert si validé */}
                                    <span
                                      className={`text-xs px-1.5 py-0.5 rounded inline-flex items-center font-bold ${
                                        isPending
                                          ? "bg-amber-100 text-amber-700"
                                          : "bg-teal-100 text-teal-700"
                                      }`}
                                      title={isPending ? (lang === "fr" ? "En attente de validation" : "Pending validation") : (lang === "fr" ? "Validé" : "Validated")}
                                      data-testid={`status-qa-${ku.id}`}
                                    >
                                      {isPending ? "..." : <CheckCircle size={12} weight="fill" />}
                                    </span>
                                  </>
                                )}
                              </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    );
                  })()}
                  {/* Vincent #106 (2026-05-08) — Le sous-onglet "Rapport" est fusionné dans "Pipeline".
                      Le bouton 📄 Rapport ouvre maintenant openReportModal('qa') qui télécharge le markdown + propose PDF. */}
                  <KnowledgeCleanupModal
                    open={cleanupOpen}
                    onClose={() => setCleanupOpen(false)}
                    kind="qa"
                    api={api}
                    videoId={videoId}
                    lang={lang}
                    onApplied={() => loadVideoData && loadVideoData()}
                  />
                </div>
              </div>
  );
};

export default QAPanel;
